import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import TemplateModal from 'components/common/modals/TemplateModal';
import Button from 'components/common/ui-kit/Button';
import { InputV2 } from 'components/common/ui-kit/Input/view';
import useAppDispatch from 'hooks/common/useAppDispatch';
import useAppSelector from 'hooks/common/useAppSelector';
import { createToast, hideModal } from 'redux/actions';
import {
  useLazyGetAllTestsQuery,
  useUpdateSchoolTestMutation,
} from 'redux/api/applications/school-test-api';
import { useUpdateEducationResultsMutation } from 'redux/api/questionnaire/questionnaire-api';
import {
  selectCurrentQuestionnaire,
  selectCurrentApplication,
} from 'redux/selectors/staff/applications';
const EditResultsModal: FC = () => {
  const dispatch = useAppDispatch();
  const closeModal = () => dispatch(hideModal());
  const modalData = useAppSelector(state => state.modal.data);
  const tests = modalData?.tests || [];
  const [testScores, setTestScores] = useState<{ [key: string]: number }>({});
  const isInitialized = useRef(false);
  const [update] = useUpdateSchoolTestMutation();

  const application = useAppSelector(selectCurrentApplication);
  const questionnaire = useAppSelector(selectCurrentQuestionnaire);
  const [updateStatistics, { isLoading }] = useUpdateEducationResultsMutation();

  const userId = questionnaire?.user.id;
  const [triggerGetEnrollee] = useLazyGetAllTestsQuery();

  useEffect(() => {
    if (tests.length > 0 && !isInitialized.current) {
      const initial: { [key: string]: number } = {};
      tests.forEach((test: any) => {
        if (test.id) {
          initial[test.id] = test.score ?? 0;
        }
      });
      setTestScores(initial);
      isInitialized.current = true;
    }
  }, [tests]);

  useEffect(() => {
    if (!modalData) {
      isInitialized.current = false;
      setTestScores({});
    }
  }, [modalData]);

  const handleScoreChange = useCallback((testId: string, value: string) => {
    let numericValue = Number(value) || 0;
    numericValue = Math.min(100, Math.max(0, numericValue));
    setTestScores(prev => ({
      ...prev,
      [testId]: numericValue,
    }));
  }, []);

  const handleSave = async () => {
    if (!userId) {
      return;
    }
    const changedTests = tests
      .filter(test => {
        const newScore = testScores[test.id];
        const originalScore = test.score ?? 0;
        return newScore !== undefined && newScore !== originalScore;
      })
      .map(test => ({
        ...test,
        newScore: testScores[test.id],
      }));

    try {
      await Promise.all(
        changedTests.map(test =>
          update({
            id: test.id,
            subjectId: test.subject?.id,
            year: test.year,
            score: test.newScore,
            isCentralTesting: test.isCentralTesting,
            centralTestingDocumentId: test.centralTestingDocumentId,
            oldDocumentRequest: test.oldDocumentResponse,
          }).unwrap()
        )
      );
      if (application) {
        triggerGetEnrollee(application.id);
      }
      closeModal();
    } catch (error) {
      dispatch(createToast('Во время сохранения произошла ошибка', 'danger'));
    }
  };

  if (!application || !questionnaire) {
    return (
      <TemplateModal title="Редактировать результаты">
        <div>Загрузка...</div>
      </TemplateModal>
    );
  }

  return (
    <TemplateModal title="Редактировать результаты">
      <div className="graduate-marks-modal">
        <div className="graduate-group">
          {tests.map(test => (
            <div key={test.id} className="test-item">
              <label>{test.subject?.title || 'Без названия'}</label>
              <InputV2
                type="number"
                min={0}
                max={100}
                value={testScores[test.id] ?? test.score ?? 0}
                onChange={e => handleScoreChange(test.id, e.target.value)}
              />
            </div>
          ))}
        </div>
        <div className="action">
          <Button theme="success" onClick={handleSave} disabled={isLoading}>
            {isLoading ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </div>
      </div>
    </TemplateModal>
  );
};

export default EditResultsModal;
